import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import StatsCard from '../../components/admin/StatsCard';
import Card from '../../components/common/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { adminService } from '../../services/admin.service';
import { theme } from '../../config/theme';
import { formatCurrency, getStatusColor } from '../../utils/helpers';
import { UsersIcon, BuildingIcon, ClockIcon, PackageIcon, ShoppingBagIcon, DollarIcon, ChartIcon, TagIcon } from '../../components/icons/Icons';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await adminService.getPlatformAnalytics();
      // ApiResponse wraps data in { statusCode, data, message }
      setAnalytics(response.data?.data || response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <MainLayout><LoadingSpinner fullScreen /></MainLayout>;

  const ordersByStatus = analytics?.orders?.byStatus || {};

  // Styles for the logo and text
  const logoTextStyles = {
    fontSize: theme.typography.fontSize['2xl'],
    fontWeight: theme.typography.fontWeight.bold,
    background: `linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.dark} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.5px',
  };

  return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl, maxWidth: '1200px', margin: '0 auto' }}>
        
        {/* Page Header with Logo, Name, and Title */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: theme.spacing.sm, 
          marginBottom: theme.spacing.xl 
        }}>
          <img 
            src="/images/logo.png" 
            alt="AlarcTrade Logo" 
            style={{ 
              height: '36px', 
              width: 'auto', 
              objectFit: 'contain' 
            }} 
          />
          <span style={logoTextStyles}>AlarcTrade</span>
          
          <span style={{ 
            fontSize: theme.typography.fontSize['2xl'], 
            color: theme.colors.gray[300], 
            margin: `0 ${theme.spacing.sm}` 
          }}>
            |
          </span>
          
          <h1 style={{ 
            fontSize: theme.typography.fontSize['2xl'], 
            fontWeight: theme.typography.fontWeight.bold, 
            color: theme.colors.text.primary,
            margin: 0
          }}>
            Admin Dashboard
          </h1>
        </div>

        {/* Primary Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: theme.spacing.lg,
          marginBottom: theme.spacing.xl,
        }}>
          <StatsCard
            title="Total Users"
            value={analytics?.users?.total || 0}
            icon={<UsersIcon size={32} />}
          />
          <StatsCard
            title="Active Sellers"
            value={analytics?.sellers?.active || 0}
            icon={<BuildingIcon size={32} />}
          />
          <StatsCard
            title="Pending Applications"
            value={analytics?.sellers?.pending || 0}
            icon={<ClockIcon size={32} />}
            onClick={() => navigate('/admin/sellers/pending')}
          />
          <StatsCard
            title="Total Products"
            value={analytics?.products?.total || 0}
            icon={<TagIcon size={32} />}
            onClick={() => navigate('/admin/products')}
          />
        </div>

        {/* Order & Revenue Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: theme.spacing.lg,
          marginBottom: theme.spacing.xl,
        }}>
          <StatsCard
            title="Total Orders"
            value={analytics?.orders?.total || 0}
            icon={<PackageIcon size={32} />}
            onClick={() => navigate('/admin/orders')}
          />
          <StatsCard
            title="Orders This Month"
            value={analytics?.orders?.newThisMonth || 0}
            icon={<ShoppingBagIcon size={32} />}
            onClick={() => navigate('/admin/orders')}
          />
          <StatsCard
            title="Total Revenue"
            value={formatCurrency(analytics?.revenue?.total || 0)}
            icon={<DollarIcon size={32} />}
          />
          <StatsCard
            title="Avg Order Value"
            value={formatCurrency(analytics?.revenue?.averageOrderValue || 0)}
            icon={<ChartIcon size={32} />}
          />
        </div>

        {/* Growth Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: theme.spacing.lg,
          marginBottom: theme.spacing.xl,
        }}>
          <StatsCard
            title="New Users (30 days)"
            value={analytics?.users?.newThisMonth || 0}
            icon={<UsersIcon size={32} />}
          />
          <StatsCard
            title="New Sellers (30 days)"
            value={analytics?.sellers?.newThisMonth || 0}
            icon={<BuildingIcon size={32} />}
          />
          <StatsCard
            title="Total Commission"
            value={formatCurrency(analytics?.revenue?.totalCommission || 0)}
            icon={<DollarIcon size={32} />}
          />
          <StatsCard
            title="Seller Earnings"
            value={formatCurrency(analytics?.revenue?.totalSellerEarnings || 0)}
            icon={<DollarIcon size={32} />}
          />
        </div>

        {/* Subscription Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: theme.spacing.lg,
          marginBottom: theme.spacing.xl,
        }}>
          <StatsCard
            title="Active Subscriptions"
            value={analytics?.subscriptions?.active || 0}
            icon={<UsersIcon size={32} />}
          />
          <StatsCard
            title="Subscription Revenue"
            value={formatCurrency(analytics?.subscriptions?.totalRevenue || 0)}
            icon={<DollarIcon size={32} />}
          />
          <StatsCard
            title="Premium Sellers"
            value={(analytics?.subscriptions?.byTier?.monthly || 0) + (analytics?.subscriptions?.byTier?.yearly || 0)}
            icon={<BuildingIcon size={32} />}
          />
          <StatsCard
            title="Free Tier Sellers"
            value={analytics?.subscriptions?.byTier?.free || 0}
            icon={<BuildingIcon size={32} />}
          />
        </div>

        {/* Orders By Status Breakdown */}
        {Object.keys(ordersByStatus).length > 0 && (
          <Card padding="lg">
            <h3 style={{
              fontSize: theme.typography.fontSize.lg,
              fontWeight: theme.typography.fontWeight.semibold,
              marginBottom: theme.spacing.lg,
            }}>
              Orders by Status
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: theme.spacing.md,
            }}>
              {Object.entries(ordersByStatus).map(([status, count]) => (
                <div key={status} style={{
                  padding: theme.spacing.md,
                  borderRadius: theme.borderRadius.lg,
                  backgroundColor: theme.colors.gray[50],
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: theme.transitions.fast,
                  border: `1px solid ${theme.colors.gray[200]}`,
                }}
                  onClick={() => navigate('/admin/orders')}
                  onMouseEnter={e => e.currentTarget.style.borderColor = getStatusColor(status)}
                  onMouseLeave={e => e.currentTarget.style.borderColor = theme.colors.gray[200]}
                >
                  <div style={{
                    fontSize: theme.typography.fontSize['2xl'],
                    fontWeight: theme.typography.fontWeight.bold,
                    color: getStatusColor(status),
                    marginBottom: 4,
                  }}>
                    {count}
                  </div>
                  <div style={{
                    fontSize: theme.typography.fontSize.xs,
                    color: theme.colors.text.secondary,
                    textTransform: 'capitalize',
                  }}>
                    {status.replace(/_/g, ' ')}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default AdminDashboard;